import React, { FC, useMemo, useCallback, memo } from 'react';
import { useLazyQuery } from '@apollo/client';
import _includes from 'lodash/includes';
import _filter from 'lodash/filter';
import _sortBy from 'lodash/sortBy';
import DropdownWithChildren from '@minecraft.dropdown-with-children';
import ErrorMessage from '@minecraft.error-message';
import { QUERY_GENDER_APPEARANCES, QUERY_GENDER_IDENTITIES } from '../FilterBasicCollectionGraphQL';
import { FilterData } from '../../../shared/types';
import { genderIdentityWhiteList } from '../../../shared/config';
import { generateSelectOptionsBasedOnField, getFilteredArrayByEnabled } from '../../../shared/utils';

type Props = {
  allowAdditionalIdentifies?: boolean;
  gender: FilterData;
  additionallyIdentifiesAs?: FilterData;
  className?: string;
};

const GenderAppearance: FC<Props> = ({
  allowAdditionalIdentifies = true,
  gender,
  additionallyIdentifiesAs,
  className,
}) => {
  const [
    getGenderApperances,
    { data: genderApperancesData, loading: genderApperancesLoading, error: genderApperancesError },
  ] = useLazyQuery(QUERY_GENDER_APPEARANCES);
  const [
    getGendedIdentities,
    { data: gendedIdentitiesData, loading: gendedIdentitiesLoading, error: gendedIdentitiesError },
  ] = useLazyQuery(QUERY_GENDER_IDENTITIES);

  const handleGenderFetch = useCallback((): void => {
    if (!genderApperancesData) {
      getGenderApperances();
    }

    if (allowAdditionalIdentifies && !gendedIdentitiesData) {
      getGendedIdentities();
    }
  }, [genderApperancesData, gendedIdentitiesData]);

  const genderOptions = useMemo(() => {
    const genderApperanceOptions = generateSelectOptionsBasedOnField(
      _sortBy(getFilteredArrayByEnabled(genderApperancesData?.genderAppearances), 'sortOrder'),
      'name',
      false,
      undefined,
      true,
      {
        fieldName: gender.name,
      }
    );
    const genderIdentityOptions = allowAdditionalIdentifies
      ? generateSelectOptionsBasedOnField(
          _sortBy(getFilteredArrayByEnabled(gendedIdentitiesData?.genderIdentities), 'sortOrder'),
          'name',
          false,
          undefined,
          true,
          {
            fieldName: additionallyIdentifiesAs.name,
            fieldLabel: additionallyIdentifiesAs.label,
          }
        )
      : [];
    const filteredGenderIdentityOptions = _filter(genderIdentityOptions, (option) =>
      _includes(genderIdentityWhiteList, option.value)
    );

    return [...genderApperanceOptions, ...(allowAdditionalIdentifies ? filteredGenderIdentityOptions : [])];
  }, [genderApperancesData, gendedIdentitiesData]);

  const error = genderApperancesError || gendedIdentitiesError;
  const loading = genderApperancesLoading || gendedIdentitiesLoading;

  if (error) return <ErrorMessage error={error} />;

  return (
    <DropdownWithChildren
      withFormik
      withSearch
      withSelectAll
      isLoading={loading}
      name={gender.name}
      toggleLabel={gender.label}
      options={genderOptions}
      onOptionsFetch={handleGenderFetch}
      className={className}
    />
  );
};

export default memo(GenderAppearance);
